import { documentsAtom, themeAtom } from "../../atoms";
import FileUploadList from "./FileUploadList";
import IconUploadDocument from "./IconUploadDocument";
import { useAtom, useAtomValue } from "jotai";
import { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const ACCEPTED_FORMATS = {
  PDF: "application/pdf",
};
const MAX_TOTAL_UPLOAD_BYTES = 50 * 1024 * 1024;
const MAX_TOTAL_UPLOAD_MB = Math.floor(MAX_TOTAL_UPLOAD_BYTES / 1024 / 1024);

export interface Props {
  uploaderClasses?: string;
  listClasses?: string;
  onFileCached?: (file: File) => void;
  progressByFileName?: Record<string, number>;
}

const FileUploader: React.FC<Props> = ({ uploaderClasses, listClasses, progressByFileName }) => {
  const theme = useAtomValue(themeAtom);

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [documents, setDocuments] = useAtom(documentsAtom);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };
  const cacheFileForUpload = async (file: File) => {
    if (file.type !== ACCEPTED_FORMATS.PDF) return;

    setDocuments((prevDocs) => {
      const fileAlreadyExists = prevDocs.some((doc) => doc.cachedFile.name === file.name);
      if (fileAlreadyExists) return prevDocs;

      const currentTotalBytes = prevDocs.reduce((totalBytes, doc) => totalBytes + doc.cachedFile.size, 0);
      if (currentTotalBytes + file.size > MAX_TOTAL_UPLOAD_BYTES) {
        setUploadError(`Total selected PDF size cannot exceed ${MAX_TOTAL_UPLOAD_MB} MB.`);
        return prevDocs;
      }

      setUploadError(null);
      return [
        ...prevDocs,
        {
          cachedFile: file,
          fileName: file.name,
          vesselCode: "",
          vesselName: "",
          imoNumber: "",
          vesselMasterEmail: "",
          docName: "",
          docType: "Certificates", // TODO Has to be dynamically filled up. Separate ticket
          source: "Manual upload",
          expiresOn: null,
          nextSurveyDate: null,
          expiryNotApplicable: false,
          portOfIssueIsWhoApproved: false,
        },
      ];
    });
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);

    Array.from(e.dataTransfer.files).forEach(async (file) => {
      cacheFileForUpload(file);
    });
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      Array.from(e.target.files).forEach((file) => cacheFileForUpload(file));
    }
    // Removing this causes a bug, the input needs to be cleared for handleFileChange to be called with the same file (we handle duplicates)
    e.target.value = "";
  };

  const removeFile = (fileName: string) => {
    setDocuments((prevDocs) => prevDocs.filter((doc) => doc.cachedFile.name !== fileName));
    setUploadError(null);
  };

  const fileUploaderClasses = twMerge(
    "mr-[136px] flex h-[293px] w-[536px] flex-shrink-0 cursor-pointer items-center rounded-md text-center",
    "border-2 border-dashed",
    theme.crtDropInput.dashedBorder,
    isDragActive && twMerge("cursor-move bg-[#3ca394]", theme.crtDropInput.dashedBg),
    uploaderClasses && `${uploaderClasses}`,
    theme.headerComponent.notifications.itemBackground,
  );

  return (
    <div className="flex">
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        className={fileUploaderClasses}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple
          onChange={handleFileChange}
          accept={Object.values(ACCEPTED_FORMATS).toString()}
          data-testid="file-uploader"
          name="file"
        />
        <div className="flex w-full flex-col items-center justify-center">
          <IconUploadDocument />
          <p className="text-md mt-2 font-bold">
            Drag & drop documents or <span className="text-[#1F8475]">Browse</span>
          </p>
          <p className="mt-2 text-xs text-[#899996]">Supported format: PDF</p>
          {uploadError && <p className="mt-2 text-xs text-[#F44C45]">{uploadError}</p>}
        </div>
      </div>
      {documents.length > 0 && (
        <FileUploadList
          files={documents.map((doc) => ({
            name: doc.cachedFile.name,
            lastModified: doc.cachedFile.lastModified,
          }))}
          removeFile={removeFile}
          errors={{}}
          progressByFileName={progressByFileName}
          className={listClasses}
        />
      )}
    </div>
  );
};

export default FileUploader;
